import { useApiClient } from '@portals-platform/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ENDPOINTS } from '../api/endpoints.js';
import type { CertificateResponse } from '../api/responses.js';
import { buildCertificateEditPayload } from '../mappers/certificate-mappers.js';
import type { CertificateInput } from '../models.js';
import { queryKeys } from '../query-keys.js';
import { isCertificateEditable } from '../rules/certificate-edit.js';

const NOT_EDITABLE_ERROR_NAME = 'CertificateNotEditableError';

export class CertificateNotEditableError extends Error {
  constructor() {
    super('This certificate can no longer be edited because it is not a draft.');
    this.name = NOT_EDITABLE_ERROR_NAME;
    Object.setPrototypeOf(this, CertificateNotEditableError.prototype);
  }
}

export const isCertificateNotEditableError = (
  error: unknown,
): error is CertificateNotEditableError =>
  error instanceof CertificateNotEditableError ||
  (error instanceof Error && error.name === NOT_EDITABLE_ERROR_NAME);

type ApiClient = ReturnType<typeof useApiClient>;

export interface EditCertificateVariables {
  certificateId: string;
  input: CertificateInput;
  captivePolicyId: string;
}

export const editCertificate = async (
  client: ApiClient,
  { certificateId, input, captivePolicyId }: EditCertificateVariables,
): Promise<CertificateResponse> => {
  const latest = await client
    .get<CertificateResponse>(ENDPOINTS.certificate(certificateId))
    .then((r) => r.data);

  if (!isCertificateEditable(latest)) {
    throw new CertificateNotEditableError();
  }

  const response = await client.put<CertificateResponse>(
    ENDPOINTS.certificate(certificateId),
    buildCertificateEditPayload(latest, input, captivePolicyId),
  );

  return response.data;
};

export function useEditCertificate() {
  const client = useApiClient();
  const queryClient = useQueryClient();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.certificates() });

  return useMutation<CertificateResponse, Error, EditCertificateVariables>({
    mutationFn: (variables) => editCertificate(client, variables),
    onSuccess: invalidate,
    onError: (error) => {
      if (isCertificateNotEditableError(error)) {
        void invalidate();
      }
    },
  });
}
